import { db, auth, functions } from '../firebaseConfig.js';
import { collection, query, where, getDocs, addDoc, serverTimestamp, orderBy, limit, startAfter, getDocsFromCache, getDocsFromServer, doc, updateDoc, increment } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { ensureFreshToken } from './authUtils.js';

// Get comments for a reason
export async function getReasonComments(reasonId, opts = {}) {
  const { pageSize = 20, cursor = null, fetchPolicy = 'cache-first' } = opts;
  const lowQuota = process.env.REACT_APP_LOW_QUOTA_MODE === 'true';
  const commentsRef = collection(db, 'solution_reason_comments');
  let q;
  try {
    // Ensure fresh auth token before reads
    await ensureFreshToken();
    q = cursor
      ? query(
          commentsRef,
          where('reasonId', '==', reasonId),
          where('parentCommentId', '==', null),
          orderBy('timestamp', 'asc'),
          startAfter(cursor),
          limit(pageSize)
        )
      : query(
          commentsRef,
          where('reasonId', '==', reasonId),
          where('parentCommentId', '==', null),
          orderBy('timestamp', 'asc'),
          limit(pageSize)
        );
    // Cache-first: try cache if requested
    if (fetchPolicy === 'cache-only' || fetchPolicy === 'cache-first') {
      try {
        const cachedSnap = await getDocsFromCache(q);
        if (!cachedSnap.empty || fetchPolicy === 'cache-only') {
          try {
            console.debug('[getReasonComments] cache snapshot', {
              reasonId,
              fromCache: cachedSnap?.metadata?.fromCache ?? 'unknown',
              size: cachedSnap?.size,
              hasPendingWrites: cachedSnap?.metadata?.hasPendingWrites ?? 'unknown'
            });
          } catch {}
          const comments = cachedSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
          const nextCursor = cachedSnap.docs.length === pageSize ? cachedSnap.docs[cachedSnap.docs.length - 1] : null;
          return { reasonId, comments, nextCursor, source: 'cache' };
        }
      } catch (_) {
        // cache miss, proceed to server
      }
      // If low quota, skip server and return empty
      if (lowQuota) {
        return { reasonId, comments: [], nextCursor: null, source: 'skipped' };
      }
    }
    // network-only forces server; otherwise prefer server to avoid staleness under persistent cache
    let snapshot;
    try {
      if (fetchPolicy === 'network-only' && typeof getDocsFromServer === 'function') {
        snapshot = await getDocsFromServer(q);
      } else {
        snapshot = await (typeof getDocsFromServer === 'function' ? getDocsFromServer(q) : getDocs(q));
      }
    } catch (e) {
      snapshot = await getDocs(q);
    }
    try {
      console.debug('[getReasonComments] server snapshot', {
        reasonId,
        fromCache: snapshot?.metadata?.fromCache ?? 'unknown',
        size: snapshot?.size,
        hasPendingWrites: snapshot?.metadata?.hasPendingWrites ?? 'unknown',
        fetchPolicy
      });
    } catch {}
    const comments = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    const nextCursor = snapshot.docs.length === pageSize ? snapshot.docs[snapshot.docs.length - 1] : null;
    return { reasonId, comments, nextCursor, source: 'server' };
  } catch (err) {
    // Fallback without orderBy (e.g., when composite index isn't created yet)
    await ensureFreshToken();
    q = cursor
      ? query(commentsRef, where('reasonId', '==', reasonId), where('parentCommentId', '==', null), startAfter(cursor), limit(pageSize))
      : query(commentsRef, where('reasonId', '==', reasonId), where('parentCommentId', '==', null), limit(pageSize));
    // Try cache first in fallback as well
    if (fetchPolicy === 'cache-only' || fetchPolicy === 'cache-first') {
      try {
        const cachedSnap = await getDocsFromCache(q);
        if (!cachedSnap.empty || fetchPolicy === 'cache-only') {
          try {
            console.debug('[getReasonComments:fallback] cache snapshot', {
              reasonId,
              fromCache: cachedSnap?.metadata?.fromCache ?? 'unknown',
              size: cachedSnap?.size,
              hasPendingWrites: cachedSnap?.metadata?.hasPendingWrites ?? 'unknown'
            });
          } catch {}
          const comments = cachedSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
          const nextCursor = cachedSnap.docs.length === pageSize ? cachedSnap.docs[cachedSnap.docs.length - 1] : null;
          return { reasonId, comments, nextCursor, source: 'cache' };
        }
      } catch (_) {}
      if (lowQuota) {
        return { reasonId, comments: [], nextCursor: null, source: 'skipped' };
      }
    }
    let snapshot;
    try {
      snapshot = await (typeof getDocsFromServer === 'function' ? getDocsFromServer(q) : getDocs(q));
    } catch (e2) {
      snapshot = await getDocs(q);
    }
    try {
      console.debug('[getReasonComments:fallback] server snapshot', {
        reasonId,
        fromCache: snapshot?.metadata?.fromCache ?? 'unknown',
        size: snapshot?.size,
        hasPendingWrites: snapshot?.metadata?.hasPendingWrites ?? 'unknown',
        fetchPolicy
      });
    } catch {}
    const comments = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    const nextCursor = snapshot.docs.length === pageSize ? snapshot.docs[snapshot.docs.length - 1] : null;
    return { reasonId, comments, nextCursor, source: 'server' };
  }
}

// Add a comment to a reason (top-level comment)
export async function addReasonComment({ reasonId, userId, userName, userProfession, text }) {
  // Basic validation and auth checks to avoid silent rule failures
  if (!reasonId || !text) {
    const err = new Error('Invalid comment payload');
    console.error('[addReasonComment] Invalid payload', { reasonId, hasText: !!text });
    throw err;
  }

  const authUid = auth?.currentUser?.uid;
  if (!authUid) {
    const err = new Error('Not authenticated');
    console.error('[addReasonComment] No authenticated Firebase user present');
    throw err;
  }
  // Always use the authenticated UID to satisfy security rules
  const uidToUse = authUid;
  if (userId && authUid !== userId) {
    console.warn('[addReasonComment] userId mismatch (overriding with auth.uid)', { providedUserId: userId, authUid });
  }

  const commentsRef = collection(db, 'solution_reason_comments');
  const payload = {
    reasonId,
    parentCommentId: null, // This marks it as a top-level comment
    userId: uidToUse,
    userName: userName ?? 'User',
    userProfession: userProfession ?? '',
    text,
    timestamp: serverTimestamp()
  };

  try {
    // Ensure the ID token is ready before Firestore write (helps right after login/refresh)
    await ensureFreshToken();
    console.debug('[addReasonComment] Writing comment', { reasonId, authUid, hasText: !!text, payloadKeys: Object.keys(payload) });
    const docRef = await addDoc(commentsRef, payload);
    // Increment commentsCount on parent reason doc via Cloud Function (bypasses owner-only rule)
    try {
      const incrementComments = httpsCallable(functions, 'incrementSolutionReasonCommentCount');
      await incrementComments({ reasonId, delta: 1 });
    } catch (e) {
      console.warn('[addReasonComment] commentsCount increment via CF failed:', e?.code || e?.message || e);
    }
    return {
      reasonId,
      comment: {
        id: docRef.id,
        reasonId,
        parentCommentId: null,
        userId: uidToUse,
        userName: payload.userName,
        userProfession: payload.userProfession,
        text,
        timestamp: new Date()
      }
    };
  } catch (error) {
    console.error('[addReasonComment] Firestore write failed', {
      code: error?.code,
      message: error?.message,
      payloadSummary: { reasonId, userId, hasText: !!text, payloadKeys: Object.keys(payload || {}) },
    });
    throw error;
  }
}
